import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { endpoints } from "../../endpoints";
import type { ReferenceEntityConstraintSchema } from "../../types/types.PIM";
import {
  getEditConstraints,
  getSelectionEntries,
  getSimilarConstraints,
} from "../../pages/SharedPages/OrganizationPage/ProductsList/ProductsList.util";
import { shouldSkipAutofillForReferenceEntityConstraint } from "../../pages/admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/allUnlessRestricted";
import { SetValueConfig } from "react-hook-form";
import { OptionType } from "../../types/types";
import { useInitializeConstraints } from ".";
import { MultiSelectRefType } from "../../pages/admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/type";

export type ConstraintOption =
  ReferenceEntityConstraintSchema["constraints"][number];

type SelectionEntry = { attribute_id: string; value: string };

type AttributeOrColumn = {
  id: string;
  input_type?: string;
  is_multiple_choice?: boolean;
  name?: string;
  display_name?: string | null;
  choices?: string[] | null;
};

/** Options shared by both single- and multi-context modes */
type BaseOptions = {
  storefrontId: string;
  enabled: boolean;
  getFormValues: () => Record<string, unknown>;
  setFormValue: (
    attributeId: string,
    value: unknown,
    config?: SetValueConfig
  ) => void;
  attributesOrColumns: AttributeOrColumn[];
  multiSelectRefs: React.MutableRefObject<Record<string, MultiSelectRefType>>;
  /** For multi_select auto-fill: 'chip' sets [{ name }], 'string' sets [value] */
  multiSelectValueFormat?: "chip" | "string";
  onConstraintsLoaded?: (constraints: ConstraintOption[]) => void;
  /** Called after the request completes (success or error), e.g. to clear loading state */
  onSettled?: () => void;
  collectionSchemaName?: string;
};

/** Single context: one group or one collection. State is a single array. */
export type UseDependentAttributesSingleOptions = BaseOptions & {
  mode: "single";
  contextType: "group" | "collection";
  contextId: string;
  /** Optional: build current selections from form values. Default: getEditConstraints(getFormValues(), constraints). */
  getCurrentSelections?: (
    formValues: Record<string, unknown>,
    constraints: ReferenceEntityConstraintSchema["constraints"]
  ) => SelectionEntry[];
};

/** Multi context: e.g. NewProductForm with multiple groups/collections per step. State is keyed by contextId. */
export type UseDependentAttributesMultiOptions = BaseOptions & {
  mode: "multi";
  /** Return the set of attribute IDs that belong to this context (used to filter form values). */
  getRelevantAttributeIds: (contextId: string) => Set<string>;
};

export type UseDependentAttributesSingleResult = {
  clearableAttributes: Set<string>;
  dependentAttributesOptions: ReferenceEntityConstraintSchema["constraints"];
  setDependentAttributesOptions: React.Dispatch<
    React.SetStateAction<ReferenceEntityConstraintSchema["constraints"]>
  >;
  updateDependentAttributes: (params?: {
    attributeId?: string;
    clearSelection?: boolean;
    clonedSelectionsValues?: Record<string, unknown>;
  }) => Promise<void>;
  resetDependentAttributes: () => void;
};

export type UseDependentAttributesMultiResult = {
  clearableAttributes: Set<string>;
  dependentAttributesByContext: Record<
    string,
    ReferenceEntityConstraintSchema["constraints"]
  >;
  updateDependentAttributes: (params: {
    contextId: string;
    contextType: "group" | "collection";
    attributeId?: string;
    clearSelection?: boolean;
    clonedSelectionsValues?: Record<string, unknown>;
  }) => Promise<void>;
  resetDependentAttributes: () => void;
};

/**
 * Merges "All Unless Restricted" valid values from the API response back into
 * the default constraints. Used in edit mode so users can still see unrestricted
 * options alongside the constrained ones.
 */
function mergeAllUnlessRestrictedValues(
  defaultConstraints: ReferenceEntityConstraintSchema["constraints"],
  responseConstraints: ReferenceEntityConstraintSchema["constraints"]
): ReferenceEntityConstraintSchema["constraints"] {
  const allUnlessRestrictedMap = new Map(
    responseConstraints.flatMap((constr) => {
      const allUnlessValue = constr.valid_values.find((vv) =>
        vv.display.includes("All Unless Restricted")
      );
      return allUnlessValue
        ? ([[constr.attribute_id, allUnlessValue]] as const)
        : [];
    })
  );

  return defaultConstraints.map((constr) => {
    const allUnlessValue = allUnlessRestrictedMap.get(constr.attribute_id);
    return allUnlessValue
      ? { ...constr, valid_values: [...constr.valid_values, allUnlessValue] }
      : constr;
  });
}

/** Clears form values and multi-select chips for a list of attribute IDs. */
function clearAttributeFields(
  attrIds: string[],
  attributesOrColumns: AttributeOrColumn[],
  setFormValue: (id: string, value: unknown) => void,
  multiSelectRefs: React.MutableRefObject<Record<string, MultiSelectRefType>>
): void {
  for (const attrId of attrIds) {
    const colType = attributesOrColumns.find(
      (c) => c.id === attrId
    )?.input_type;
    multiSelectRefs.current[attrId]?.clearChips?.();
    setFormValue(attrId, colType === "multi_select" ? [] : null);
  }
}

function applyConstraintActions(
  constraints: ConstraintOption[],
  attributesOrColumns: AttributeOrColumn[],
  setFormValue: (
    attributeId: string,
    value: unknown,
    config?: SetValueConfig
  ) => void,
  multiSelectRefs: React.MutableRefObject<Record<string, MultiSelectRefType>>,
  multiSelectValueFormat: "chip" | "string" = "chip",
  getFormValues: () => Record<string, unknown>,
  collectionSchemaName?: string
) {
  const attrTypeMap = new Map(
    attributesOrColumns.map((col) => [col.id, col.input_type])
  );

  for (const constraint of constraints) {
    const colType = attrTypeMap.get(constraint.attribute_id);

    const formValue = getFormValues()[constraint.attribute_id] as
      | OptionType<string>
      | { name: string }[]
      | string[]
      | undefined;

    // action "filter" with one value must not write the form (incomplete RE rows).
    // Other responses still autofill on a single valid value / value_to_set_display.
    const wantsAutoFill =
      constraint.action === "auto_fill" ||
      (constraint.action !== "filter" &&
        (!!constraint.value_to_set_display ||
          constraint.valid_values.length === 1));

    const shouldAutoFill =
      wantsAutoFill &&
      !shouldSkipAutofillForReferenceEntityConstraint(
        collectionSchemaName,
        constraints,
        attributesOrColumns,
        constraint,
        constraint.valid_values.length
      );
    if (shouldAutoFill) {
      const valueToDisplay =
        constraint.value_to_set_display ||
        (constraint.valid_values[0] && constraint.valid_values[0].display);

      if (
        colType === "single_select" ||
        colType === "product_reference" ||
        colType === "object_reference" ||
        colType === "user"
      ) {
        setFormValue(
          constraint.attribute_id,
          { label: valueToDisplay, value: valueToDisplay },
          { shouldValidate: true }
        );
      } else if (colType === "multi_select") {
        multiSelectRefs.current[constraint.attribute_id]?.setChips([
          { name: valueToDisplay },
        ]);
        setFormValue(
          constraint.attribute_id,
          multiSelectValueFormat === "chip"
            ? [{ name: valueToDisplay }]
            : [valueToDisplay],
          { shouldValidate: true }
        );
      } else {
        setFormValue(constraint.attribute_id, valueToDisplay, {
          shouldValidate: true,
        });
      }
      continue;
    }

    const shouldClear =
      constraint.action === "clear" ||
      constraint.action === "filter_and_clear" ||
      (constraint.action === "filter" &&
        (() => {
          if (Array.isArray(formValue)) {
            return (
              formValue.length > 0 &&
              constraint.valid_values.every((valid) =>
                (formValue as Array<string | { name: string }>).every((item) =>
                  typeof item === "string"
                    ? item !== valid.display
                    : item.name !== valid.display
                )
              )
            );
          }
          return (
            !!(formValue && (formValue as OptionType<string>).label) &&
            constraint.valid_values.every(
              (valid) =>
                valid.display !== (formValue as OptionType<string>).label
            )
          );
        })());

    if (shouldClear) {
      const nullValue = colType === "multi_select" ? [] : null;
      setFormValue(constraint.attribute_id, nullValue);
      multiSelectRefs.current[constraint.attribute_id]?.clearChips?.();
      continue;
    }

    if (colType === "multi_select" && Array.isArray(formValue)) {
      const filteredFormValue = (
        formValue as Array<string | { name: string }>
      ).filter((item) =>
        constraint.valid_values.some((value) =>
          typeof item === "string"
            ? item === value.display
            : item.name === value.display
        )
      );
      setFormValue(constraint.attribute_id, filteredFormValue, {
        shouldValidate: true,
      });
      multiSelectRefs.current[constraint.attribute_id]?.setChips(
        filteredFormValue.map((item) =>
          typeof item === "string" ? { name: item } : item
        )
      );
    }
  }
}

function buildCurrentSelectionsForMultiContext(
  formValues: Record<string, unknown>,
  relevantAttributeIds: Set<string>
): SelectionEntry[] {
  return Object.entries(formValues ?? {}).flatMap(([key, value]) =>
    relevantAttributeIds.has(key) ? getSelectionEntries(key, value as any) : []
  );
}

type BuildCurrentSelectionsParams = {
  clonedSelectionsValues?: Record<string, unknown>;
  formValues: Record<string, unknown>;
  clearableAttributes: Set<string>;
  attributeId?: string;
  clearSelection: boolean;
  defaultConstraints: ReferenceEntityConstraintSchema["constraints"];
  getCurrentSelections?: UseDependentAttributesSingleOptions["getCurrentSelections"];
};

function buildCurrentSelections({
  clonedSelectionsValues,
  formValues,
  clearableAttributes,
  attributeId,
  clearSelection,
  defaultConstraints,
  getCurrentSelections,
}: BuildCurrentSelectionsParams): SelectionEntry[] {
  if (clonedSelectionsValues) {
    return getEditConstraints(clonedSelectionsValues, defaultConstraints);
  }

  const selectedAttributes = (
    clearSelection && attributeId
      ? Array.from(clearableAttributes).filter((attr) => attr !== attributeId)
      : attributeId
      ? [...clearableAttributes, attributeId]
      : [...clearableAttributes]
  ).map((attr) => ({ attribute_id: attr }));

  return getCurrentSelections
    ? getCurrentSelections(
        formValues,
        selectedAttributes as unknown as ReferenceEntityConstraintSchema["constraints"]
      )
    : getEditConstraints(
        formValues as Record<string, any>,
        selectedAttributes as unknown as ReferenceEntityConstraintSchema["constraints"]
      );
}

/** Internal hook for single-context mode. Called with null when mode is multi. */
function useDependentAttributesSingle(
  options: UseDependentAttributesSingleOptions | null,
  requestIdRef: React.MutableRefObject<number>,
  multiSelectValueFormat: "chip" | "string"
): UseDependentAttributesSingleResult {
  const [constraints, setConstraints] = useState<
    ReferenceEntityConstraintSchema["constraints"]
  >([]);
  const [dependentAttributesOptions, setDependentAttributesOptions] = useState<
    ReferenceEntityConstraintSchema["constraints"]
  >([]);
  const [clearableAttributes, setClearableAttributes] = useState(
    () => new Set<string>()
  );
  const [autofillMapping, setAutofillMapping] = useState(
    () => new Map<string, ReferenceEntityConstraintSchema["auto_fills"]>()
  );

  // Refs keep callbacks stable — no state in useCallback deps
  const constraintsRef = useRef(constraints);
  constraintsRef.current = constraints;
  const clearableAttributesRef = useRef(clearableAttributes);
  clearableAttributesRef.current = clearableAttributes;
  const autofillMappingRef = useRef(autofillMapping);
  autofillMappingRef.current = autofillMapping;

  const initializeConstraints = useInitializeConstraints();
  const initializeConstraintsRef = useRef(initializeConstraints);
  initializeConstraintsRef.current = initializeConstraints;

  const multiSelectValueFormatRef = useRef(multiSelectValueFormat);
  multiSelectValueFormatRef.current = multiSelectValueFormat;

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const isEditingRef = useRef(false);

  const updateDependentAttributes = useCallback(
    async (params?: {
      attributeId?: string;
      clearSelection?: boolean;
      /** Pre-existing values for edit mode; used instead of form values for constraint calculation */
      clonedSelectionsValues?: Record<string, unknown>;
    }) => {
      const opts = optionsRef.current;
      if (!opts?.enabled || !opts.contextId) return;

      const {
        storefrontId,
        contextType,
        contextId,
        getFormValues,
        setFormValue,
        attributesOrColumns,
        multiSelectRefs,
        getCurrentSelections,
        onConstraintsLoaded,
        onSettled,
        collectionSchemaName,
      } = opts;

      const attributeId = params?.attributeId;
      const clearSelection = params?.clearSelection ?? false;
      const clonedSelectionsValues = params?.clonedSelectionsValues;

      // Ensure constraints are initialized
      let defaultConstraints = constraintsRef.current;
      if (!defaultConstraints.length) {
        defaultConstraints =
          (await initializeConstraintsRef.current(
            true,
            contextId,
            contextType
          )) ?? [];
        setConstraints(defaultConstraints);
      }

      const requestId = ++requestIdRef.current;
      const formValues = getFormValues();

      const currentSelections = buildCurrentSelections({
        clonedSelectionsValues,
        formValues,
        clearableAttributes: clearableAttributesRef.current,
        attributeId,
        clearSelection,
        defaultConstraints,
        getCurrentSelections,
      });

      if (clonedSelectionsValues) {
        isEditingRef.current = true;
      } else if (!clearSelection && currentSelections.length) {
        isEditingRef.current = false;
      }

      if (attributeId) {
        if (clearSelection) {
          const constraintsToClear = isEditingRef.current
            ? getSimilarConstraints(defaultConstraints, attributeId)
            : (autofillMappingRef.current.get(attributeId) ?? []).map(
                ({ attribute_id }) => attribute_id
              );

          clearAttributeFields(
            constraintsToClear,
            attributesOrColumns,
            setFormValue,
            multiSelectRefs
          );

          setClearableAttributes((prev) => {
            const next = new Set(prev);
            next.delete(attributeId);
            return next;
          });
          setAutofillMapping((prev) => {
            const next = new Map(prev);
            next.delete(attributeId);
            return next;
          });
          isEditingRef.current = false;
        } else {
          setClearableAttributes((prev) =>
            prev.has(attributeId) ? prev : new Set(prev).add(attributeId)
          );
        }
      } else {
        setClearableAttributes(new Set());
      }

      const applyAndNotify = (
        constraintOptions: ReferenceEntityConstraintSchema["constraints"]
      ) => {
        applyConstraintActions(
          constraintOptions,
          attributesOrColumns,
          setFormValue,
          multiSelectRefs,
          multiSelectValueFormatRef.current,
          getFormValues,
          collectionSchemaName
        );
        setDependentAttributesOptions(constraintOptions);
        onConstraintsLoaded?.(constraintOptions);
      };

      if (currentSelections.length === 0) {
        applyAndNotify(defaultConstraints);
        onSettled?.();
        return;
      }

      try {
        const { data } = await axios.post<ReferenceEntityConstraintSchema>(
          endpoints.v2_storefronts_id_reference_entity_calculate_constraints(
            storefrontId
          ),
          {
            context_type: contextType,
            context_id: contextId,
            active_attribute_id: attributeId,
            current_selections: currentSelections,
          }
        );
        if (requestId !== requestIdRef.current) return;

        applyAndNotify(
          isEditingRef.current
            ? mergeAllUnlessRestrictedValues(
                defaultConstraints,
                data.constraints
              )
            : data.constraints
        );

        if (
          attributeId &&
          Array.isArray(data.auto_fills) &&
          data.auto_fills.length > 0
        ) {
          setAutofillMapping((prev) =>
            new Map(prev).set(attributeId, data.auto_fills)
          );
        }
      } catch {
        // Constraint calculation errors are non-critical: ignore
      } finally {
        onSettled?.();
      }
    },
    [requestIdRef]
  );

  const resetDependentAttributes = useCallback(() => {
    setDependentAttributesOptions([]);
    setClearableAttributes(new Set());
    setAutofillMapping(new Map());
  }, []);

  return {
    clearableAttributes,
    dependentAttributesOptions,
    setDependentAttributesOptions,
    updateDependentAttributes,
    resetDependentAttributes,
  };
}

/** Internal hook for multi-context mode. Called with null when mode is single. */
function useDependentAttributesMulti(
  options: UseDependentAttributesMultiOptions | null,
  multiSelectValueFormat: "chip" | "string"
): UseDependentAttributesMultiResult {
  const [constraintsByContext, setConstraintsByContext] = useState<
    Record<string, ReferenceEntityConstraintSchema["constraints"]>
  >({});
  const [dependentAttributesByContext, setDependentAttributesByContext] =
    useState<Record<string, ReferenceEntityConstraintSchema["constraints"]>>(
      {}
    );
  const [clearableAttributes, setClearableAttributes] = useState<Set<string>>(
    new Set()
  );
  const [autofillMappingByContext, setAutofillMappingByContext] = useState<
    Record<string, Map<string, ReferenceEntityConstraintSchema["auto_fills"]>>
  >({});

  // Refs keep callbacks stable — no state in useCallback deps
  const constraintsByContextRef = useRef(constraintsByContext);
  constraintsByContextRef.current = constraintsByContext;
  const clearableAttributesRef = useRef(clearableAttributes);
  clearableAttributesRef.current = clearableAttributes;
  const autofillMappingByContextRef = useRef(autofillMappingByContext);
  autofillMappingByContextRef.current = autofillMappingByContext;

  // Per-context request IDs to prevent stale responses from parallel context requests
  const requestIdByContextRef = useRef<Record<string, number>>({});
  // Per-context editing flag (set when clonedSelectionsValues is provided)
  const isEditingByContextRef = useRef<Record<string, boolean>>({});

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const initializeConstraints = useInitializeConstraints();
  const initializeConstraintsRef = useRef(initializeConstraints);
  initializeConstraintsRef.current = initializeConstraints;

  const multiSelectValueFormatRef = useRef(multiSelectValueFormat);
  multiSelectValueFormatRef.current = multiSelectValueFormat;

  const updateDependentAttributes = useCallback(
    async (params: {
      contextId: string;
      contextType: "group" | "collection";
      attributeId?: string;
      clearSelection?: boolean;
      clonedSelectionsValues?: Record<string, unknown>;
    }) => {
      const opts = optionsRef.current;
      if (!opts?.enabled) return;

      const {
        storefrontId,
        getFormValues,
        setFormValue,
        attributesOrColumns,
        multiSelectRefs,
        getRelevantAttributeIds,
        onConstraintsLoaded,
        onSettled,
        collectionSchemaName,
      } = opts;

      const { contextId, contextType } = params;
      const attributeId = params.attributeId;
      const clearSelection = params.clearSelection ?? false;
      const clonedSelectionsValues = params.clonedSelectionsValues;

      // Ensure constraints are initialized for this context
      const cachedConstraints =
        constraintsByContextRef.current[contextId] ?? [];
      let defaultConstraints = cachedConstraints;
      if (!cachedConstraints.length) {
        defaultConstraints =
          (await initializeConstraintsRef.current(
            true,
            contextId,
            contextType
          )) ?? [];
        setConstraintsByContext((prev) => ({
          ...prev,
          [contextId]: defaultConstraints,
        }));
      }

      // Per-context race condition guard
      const contextRequestId = (requestIdByContextRef.current[contextId] =
        (requestIdByContextRef.current[contextId] ?? 0) + 1);

      const formValues = getFormValues();
      const relevantIds = new Set(
        defaultConstraints
          .map((c) => c.attribute_id)
          .filter((id) => getRelevantAttributeIds(contextId).has(id))
      );
      const contextAutofillMapping =
        autofillMappingByContextRef.current[contextId] ?? new Map();

      if (clonedSelectionsValues) {
        isEditingByContextRef.current[contextId] = true;
      }

      // Build current selections before any state updates
      const currentSelections = buildCurrentSelectionsForContext(
        clonedSelectionsValues,
        formValues,
        clearableAttributesRef.current,
        relevantIds,
        attributeId,
        clearSelection
      );

      if (attributeId) {
        if (clearSelection) {
          const constraintsToClear = isEditingByContextRef.current[contextId]
            ? getSimilarConstraints(defaultConstraints, attributeId)
            : (contextAutofillMapping.get(attributeId) ?? []).map(
                ({ attribute_id }) => attribute_id
              );

          clearAttributeFields(
            constraintsToClear,
            attributesOrColumns,
            setFormValue,
            multiSelectRefs
          );

          setClearableAttributes((prev) => {
            const next = new Set(prev);
            next.delete(attributeId);
            return next;
          });
          setAutofillMappingByContext((prev) => {
            const contextMap = new Map(prev[contextId] ?? []);
            contextMap.delete(attributeId);
            return { ...prev, [contextId]: contextMap };
          });
          isEditingByContextRef.current[contextId] = false;
        } else {
          setClearableAttributes((prev) =>
            prev.has(attributeId) ? prev : new Set(prev).add(attributeId)
          );
        }
      } else {
        // No attributeId (e.g. initial load): reset clearable attributes for this context only
        setClearableAttributes((prev) => {
          const next = new Set(prev);
          relevantIds.forEach((id) => next.delete(id));
          return next;
        });
      }

      const applyAndNotify = (
        constraintOptions: ReferenceEntityConstraintSchema["constraints"]
      ) => {
        applyConstraintActions(
          constraintOptions,
          attributesOrColumns,
          setFormValue,
          multiSelectRefs,
          multiSelectValueFormatRef.current,
          getFormValues,
          collectionSchemaName
        );
        setDependentAttributesByContext((prev) => ({
          ...prev,
          [contextId]: constraintOptions,
        }));
        onConstraintsLoaded?.(constraintOptions);
      };

      if (currentSelections.length === 0) {
        applyAndNotify(defaultConstraints);
        onSettled?.();
        return;
      }

      try {
        const { data } = await axios.post<ReferenceEntityConstraintSchema>(
          endpoints.v2_storefronts_id_reference_entity_calculate_constraints(
            storefrontId
          ),
          {
            context_type: contextType,
            context_id: contextId,
            active_attribute_id: attributeId,
            current_selections: currentSelections,
          }
        );
        if (contextRequestId !== requestIdByContextRef.current[contextId]) {
          return;
        }

        applyAndNotify(
          isEditingByContextRef.current[contextId]
            ? mergeAllUnlessRestrictedValues(
                defaultConstraints,
                data.constraints
              )
            : data.constraints
        );

        if (
          attributeId &&
          Array.isArray(data.auto_fills) &&
          data.auto_fills.length > 0
        ) {
          setAutofillMappingByContext((prev) => {
            const contextMap = new Map(prev[contextId] ?? []);
            return {
              ...prev,
              [contextId]: contextMap.set(attributeId, data.auto_fills),
            };
          });
        }
      } catch {
        // Constraint calculation errors are non-critical: ignore
      } finally {
        onSettled?.();
      }
    },
    []
  );

  const resetDependentAttributes = useCallback(() => {
    setDependentAttributesByContext({});
    setConstraintsByContext({});
    setClearableAttributes(new Set());
    setAutofillMappingByContext({});
    requestIdByContextRef.current = {};
    isEditingByContextRef.current = {};
  }, []);

  return {
    clearableAttributes,
    dependentAttributesByContext,
    updateDependentAttributes,
    resetDependentAttributes,
  };
}

/** Builds current selections for a single context in multi mode. */
function buildCurrentSelectionsForContext(
  clonedSelectionsValues: Record<string, unknown> | undefined,
  formValues: Record<string, unknown>,
  clearableAttributes: Set<string>,
  relevantIds: Set<string>,
  attributeId: string | undefined,
  clearSelection: boolean
): SelectionEntry[] {
  if (clonedSelectionsValues) {
    return buildCurrentSelectionsForMultiContext(
      clonedSelectionsValues,
      relevantIds
    );
  }

  const contextClearable = Array.from(clearableAttributes).filter((id) =>
    relevantIds.has(id)
  );
  const activeAttributes =
    clearSelection && attributeId
      ? contextClearable.filter((attr) => attr !== attributeId)
      : attributeId
      ? [...contextClearable, attributeId]
      : contextClearable;

  return activeAttributes.flatMap((attrId) =>
    getSelectionEntries(attrId, formValues[attrId] as any)
  );
}

/**
 * Consolidated hook for reference-entity dependent attribute constraint calculation.
 * Supports single context (one group/collection) and multi context (e.g. product form with multiple groups/collections).
 * Uses stable callbacks via refs to avoid re-creating functions on state changes.
 * Both single and multi hooks are invoked in the same order every render; return value depends on options.mode.
 */
export function useDependentAttributes(
  options: UseDependentAttributesSingleOptions
): UseDependentAttributesSingleResult;
export function useDependentAttributes(
  options: UseDependentAttributesMultiOptions
): UseDependentAttributesMultiResult;
export function useDependentAttributes(
  options:
    | UseDependentAttributesSingleOptions
    | UseDependentAttributesMultiOptions
): UseDependentAttributesSingleResult | UseDependentAttributesMultiResult {
  const requestIdRef = useRef(0);
  const multiSelectValueFormat = options.multiSelectValueFormat ?? "chip";

  const singleOptions: UseDependentAttributesSingleOptions | null =
    options.mode === "single" ? options : null;
  const multiOptions: UseDependentAttributesMultiOptions | null =
    options.mode === "multi" ? options : null;

  const singleResult = useDependentAttributesSingle(
    singleOptions,
    requestIdRef,
    multiSelectValueFormat
  );
  const multiResult = useDependentAttributesMulti(
    multiOptions,
    multiSelectValueFormat
  );

  return options.mode === "single" ? singleResult : multiResult;
}
